import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import type { NodeKey } from 'lexical';
import { CLICK_COMMAND, COMMAND_PRIORITY_LOW } from 'lexical';
import { useEffect, useRef, useState } from 'react';

import { useEditorActive } from '../../hooks/useEditorActive';
import { ImageResizeHandles } from './ImageResizeHandles';
import { ImageToolbar } from './ImageToolbar';

type ImageComponentProps = {
  nodeKey: NodeKey;
  src: string;
  altText: string;
  width?: number;
};

/**
 * What an `ImageNode` renders: the image, selectable by click.
 * - While selected in an active editor: corner resize handles and the floating `ImageToolbar`.
 * - Delete/Backspace on a selected image is handled by `RichTextPlugin`.
 */
export const ImageComponent = ({ nodeKey, src, altText, width }: ImageComponentProps) => {
  const [editor] = useLexicalComposerContext();
  const editorActive = useEditorActive();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [draftWidth, setDraftWidth] = useState<number | null>(null);

  useEffect(
    () =>
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          // Clicks on the handles count too, so a resize never drops the selection
          if (!(event.target instanceof Node) || !wrapperRef.current?.contains(event.target)) return false;
          if (!event.shiftKey) clearSelection();
          setSelected(true);
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [clearSelection, editor, setSelected],
  );

  const active = editorActive && isSelected;

  return (
    <span ref={wrapperRef} className="relative inline-block max-w-full align-baseline">
      <img
        ref={setImage}
        src={src}
        alt={altText}
        width={draftWidth ?? width}
        draggable={false}
        className={`inline-block max-w-full align-baseline ${active ? 'outline-accent-1-100 outline-2 outline-offset-2' : ''}`}
      />

      {active && image && <ImageResizeHandles nodeKey={nodeKey} image={image} onDraftWidth={setDraftWidth} />}
      {active && <ImageToolbar nodeKey={nodeKey} altText={altText} width={width} />}
    </span>
  );
};
